/**
 * Data Access Request Page - Submit and track data access requests
 */

import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface AccessRequest {
  id: string;
  key: string;
  summary: string;
  status: string;
  status_category: string;
  created: string;
  updated: string;
  priority?: string;
}

interface FormData {
  department: string;
  requested_data_types: string[];
  data_type_justification: string;
  specific_devices: string;
  specific_clinics: string;
  specific_studies: string;
  research_purpose: string;
  irb_approval: string;
  irb_approval_details: string;
  data_retention_period: string;
  access_type: string;
  access_end_date: string;
  data_usage_agreement: boolean;
  privacy_policy_agreement: boolean;
  data_security_agreement: boolean;
  priority: string;
}

const DATA_TYPES = [
  'ECG (Electrocardiogram)',
  'Accelerometer',
  'Temperature',
  'HRV (Heart Rate Variability)',
  'AFib (Atrial Fibrillation)',
  'PPG (Photoplethysmography)',
];

const IRB_OPTIONS = [
  'Approved (provide details below)',
  'Pending approval',
  'Not applicable',
  'Exempt',
];

const RETENTION_PERIODS = ['3 months', '6 months', '1 year', '2 years', 'Duration of study', 'Permanent (requires special approval)'];

const ACCESS_TYPES = ['Temporary (specify end date)', 'Permanent (requires justification)'];

export default function AccessRequestPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'form' | 'status'>('form');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [myRequests, setMyRequests] = useState<AccessRequest[]>([]);
  const [validated, setValidated] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>('');

  const [formData, setFormData] = useState<FormData>({
    department: '',
    requested_data_types: [],
    data_type_justification: '',
    specific_devices: '',
    specific_clinics: '',
    specific_studies: '',
    research_purpose: '',
    irb_approval: '',
    irb_approval_details: '',
    data_retention_period: '',
    access_type: '',
    access_end_date: '',
    data_usage_agreement: false,
    privacy_policy_agreement: false,
    data_security_agreement: false,
    priority: 'Medium',
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Load user's previous requests
  useEffect(() => {
    if (activeTab === 'status') {
      loadMyRequests();
    }
  }, [activeTab]);

  const loadMyRequests = async () => {
    setStatusLoading(true);
    try {
      const response = await apiClient.getMyAccessRequests();
      if (response.error) {
        toast.error(t('accessRequest.messages.loadError', { error: response.error }));
      } else if (response.data) {
        const data = response.data as { success: boolean; requests?: Array<AccessRequest>; error?: string };
        if (data.requests) {
          setMyRequests(data.requests);
        }
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error(t('accessRequest.messages.loadError', { error: 'Unknown error' }));
    } finally {
      setStatusLoading(false);
    }
  };

  const refreshRequestStatus = async (ticketId: string) => {
    try {
      const response = await apiClient.getAccessRequestStatus(ticketId);
      if (response.error) {
        toast.error(t('accessRequest.messages.refreshError'));
      } else if (response.data) {
        // Update the specific request in the list
        setMyRequests((prev) => prev.map((req) => (req.key === ticketId ? (response.data as AccessRequest) : req)));
        toast.success(t('accessRequest.messages.refreshSuccess'));
      }
    } catch (error) {
      console.error('Error refreshing status:', error);
      toast.error(t('accessRequest.messages.refreshError'));
    }
  };

  const handleCheckboxChange = (dataType: string) => {
    setFormData((prev) => ({
      ...prev,
      requested_data_types: prev.requested_data_types.includes(dataType)
        ? prev.requested_data_types.filter((dt) => dt !== dataType)
        : [...prev.requested_data_types, dataType],
    }));
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Validate at least one data type selected
    if (formData.requested_data_types.length === 0) {
      toast.error(t('accessRequest.form.requestedDataTypesHelp'));
      return;
    }

    // Validate all agreements are checked
    if (!formData.data_usage_agreement || !formData.privacy_policy_agreement || !formData.data_security_agreement) {
      toast.error(t('accessRequest.form.acceptAllAgreements'));
      return;
    }

    setLoading(true);
    setProgressMessage('Preparing your request...');
    try {
      const payload = {
        requester_email: user?.email || '',
        ...formData,
      };

      setProgressMessage('Submitting your access request...');
      const response = await apiClient.createAccessRequest(payload, uploadedFile || undefined);

      if (response.error) {
        setProgressMessage('');
        toast.error(t('accessRequest.messages.error', { error: response.error }));
      } else if (response.data) {
        const data = response.data as { success: boolean; message: string; ticket_id?: string };
        setProgressMessage('Request created successfully! Sending confirmation emails...');

        // Small delay to show the success message
        await new Promise(resolve => setTimeout(resolve, 500));

        setProgressMessage('');
        toast.success(t('accessRequest.messages.success', { ticketId: data.ticket_id || 'N/A' }), { duration: 6000 });

        // Reset form
        setFormData({
          department: '',
          requested_data_types: [],
          data_type_justification: '',
          specific_devices: '',
          specific_clinics: '',
          specific_studies: '',
          research_purpose: '',
          irb_approval: '',
          irb_approval_details: '',
          data_retention_period: '',
          access_type: '',
          access_end_date: '',
          data_usage_agreement: false,
          privacy_policy_agreement: false,
          data_security_agreement: false,
          priority: 'Medium',
        });
        setUploadedFile(null);
        setValidated(false);

        // Switch to status tab
        setActiveTab('status');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setProgressMessage('');
      toast.error(t('accessRequest.messages.error', { error: 'Unexpected error occurred' }));
    } finally {
      setLoading(false);
      setProgressMessage('');
    }
  };

  const getStatusBadgeVariant = (statusCategory: string) => {
    switch (statusCategory.toLowerCase()) {
      case 'done':
        return 'success';
      case 'in progress':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-2">{t('accessRequest.title')}</h1>
          <p className="text-muted">Submit a request to access EDN Data Hub datasets for your research</p>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Row className="mb-4">
        <Col>
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${activeTab === 'form' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab('form')}
            >
              <i className="fas fa-file-alt me-2"></i>
              {t('accessRequest.newRequest')}
            </button>
            <button
              type="button"
              className={`btn ${activeTab === 'status' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setActiveTab('status')}
            >
              <i className="fas fa-list me-2"></i>
              {t('accessRequest.myRequests')}
            </button>
          </div>
        </Col>
      </Row>

      {/* Form Tab */}
      {activeTab === 'form' && (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Requester Information */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Requester Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('accessRequest.form.requesterEmail')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control type="email" value={user?.email || ''} disabled />
                    <Form.Text className="text-muted">{t('accessRequest.form.requesterEmailHelp')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('accessRequest.form.department')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('accessRequest.form.departmentPlaceholder')}
                      value={formData.department}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Access Scope - Data Types */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Access Scope - Data Types</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('accessRequest.form.requestedDataTypes')} <span className="text-danger">*</span>
                </Form.Label>
                <div>
                  {DATA_TYPES.map((dataType) => (
                    <Form.Check
                      key={dataType}
                      type="checkbox"
                      id={`dataType-${dataType}`}
                      label={dataType}
                      checked={formData.requested_data_types.includes(dataType)}
                      onChange={() => handleCheckboxChange(dataType)}
                      className="mb-2"
                    />
                  ))}
                </div>
                {formData.requested_data_types.length === 0 && validated && (
                  <div className="text-danger small">Please select at least one data type</div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  {t('accessRequest.form.dataTypeJustification')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t('accessRequest.form.dataTypeJustificationPlaceholder')}
                  value={formData.data_type_justification}
                  onChange={(e) => handleInputChange('data_type_justification', e.target.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Access Filters (Optional) */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Access Filters (Optional)</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('accessRequest.form.specificDevices')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('accessRequest.form.specificDevicesPlaceholder')}
                      value={formData.specific_devices}
                      onChange={(e) => handleInputChange('specific_devices', e.target.value)}
                    />
                    <Form.Text className="text-muted">Comma-separated device IDs. Leave blank for all authorized devices.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('accessRequest.form.specificClinics')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('accessRequest.form.specificClinicsPlaceholder')}
                      value={formData.specific_clinics}
                      onChange={(e) => handleInputChange('specific_clinics', e.target.value)}
                    />
                    <Form.Text className="text-muted">Comma-separated clinic names. Leave blank for all authorized clinics.</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('accessRequest.form.specificStudies')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('accessRequest.form.specificStudiesPlaceholder')}
                      value={formData.specific_studies}
                      onChange={(e) => handleInputChange('specific_studies', e.target.value)}
                    />
                    <Form.Text className="text-muted">Comma-separated study IDs. Leave blank for all authorized studies.</Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Upload File (Optional)</Form.Label>
                    <Form.Control
                      type="file"
                      onChange={(e) => {
                        const file = (e.target as HTMLInputElement).files?.[0] || null;
                        setUploadedFile(file);
                      }}
                      accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
                    />
                    <Form.Text className="text-muted">
                      Upload a file to attach to your access request (e.g., IRB approval document, research proposal, etc.).
                      Accepted formats: PDF, DOC, DOCX, TXT, CSV, XLSX, XLS, PNG, JPG, JPEG.
                      {uploadedFile && (
                        <span className="d-block mt-1 text-success">
                          <i className="fas fa-check-circle me-1"></i>
                          Selected: {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(2)} KB)
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Purpose & Compliance */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Purpose & Compliance</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>
                  {t('accessRequest.form.researchPurpose')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder={t('accessRequest.form.researchPurposePlaceholder')}
                  value={formData.research_purpose}
                  onChange={(e) => handleInputChange('research_purpose', e.target.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('accessRequest.form.irbApproval')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select value={formData.irb_approval} onChange={(e) => handleInputChange('irb_approval', e.target.value)} required>
                      <option value="">{t('accessRequest.form.irbApprovalPlaceholder')}</option>
                      {IRB_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('accessRequest.form.dataRetentionPeriod')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={formData.data_retention_period}
                      onChange={(e) => handleInputChange('data_retention_period', e.target.value)}
                      required
                    >
                      <option value="">{t('accessRequest.form.dataRetentionPeriodPlaceholder')}</option>
                      {RETENTION_PERIODS.map((period) => (
                        <option key={period} value={period}>
                          {period}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {formData.irb_approval === 'Approved (provide details below)' && (
                <Form.Group className="mb-3">
                  <Form.Label>{t('accessRequest.form.irbApprovalDetails')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder={t('accessRequest.form.irbApprovalDetailsPlaceholder')}
                    value={formData.irb_approval_details}
                    onChange={(e) => handleInputChange('irb_approval_details', e.target.value)}
                  />
                </Form.Group>
              )}
            </Card.Body>
          </Card>

          {/* Access Duration */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Access Duration</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('accessRequest.form.accessType')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select value={formData.access_type} onChange={(e) => handleInputChange('access_type', e.target.value)} required>
                      <option value="">{t('accessRequest.form.accessTypePlaceholder')}</option>
                      {ACCESS_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{t('accessRequest.form.allFieldsRequired')}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  {formData.access_type === 'Temporary (specify end date)' && (
                    <Form.Group className="mb-3">
                      <Form.Label>{t('accessRequest.form.accessEndDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.access_end_date}
                        onChange={(e) => handleInputChange('access_end_date', e.target.value)}
                      />
                      <Form.Text className="text-muted">{t('accessRequest.form.accessEndDateHelp')}</Form.Text>
                    </Form.Group>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Agreements */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Required Agreements</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Please read and accept the following agreements before submitting your request.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="dataUsageAgreement"
                  label={
                    <span>
                      I agree to use this data only for the stated research purpose and in compliance with all applicable privacy regulations (HIPAA,
                      GDPR, PIPEDA, etc.) <span className="text-danger">*</span>
                    </span>
                  }
                  checked={formData.data_usage_agreement}
                  onChange={(e) => handleInputChange('data_usage_agreement', e.target.checked)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="privacyPolicyAgreement"
                  label={
                    <span>
                      I acknowledge that I have read and agree to the EDN Data Hub Privacy Policy <span className="text-danger">*</span>
                    </span>
                  }
                  checked={formData.privacy_policy_agreement}
                  onChange={(e) => handleInputChange('privacy_policy_agreement', e.target.checked)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="dataSecurityAgreement"
                  label={
                    <span>
                      I agree to maintain appropriate security measures for any data accessed or downloaded, including encryption and secure storage{' '}
                      <span className="text-danger">*</span>
                    </span>
                  }
                  checked={formData.data_security_agreement}
                  onChange={(e) => handleInputChange('data_security_agreement', e.target.checked)}
                  required
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Progress Message */}
          {loading && progressMessage && (
            <Alert variant="info" className="mb-3">
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              {progressMessage}
            </Alert>
          )}

          {/* Submit Button */}
          <div className="d-grid gap-2">
            <Button type="submit" variant="primary" size="lg" disabled={loading}>
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {progressMessage || t('accessRequest.messages.submitting')}
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  {t('accessRequest.submitRequest')}
                </>
              )}
            </Button>
          </div>
        </Form>
      )}

      {/* Status Tab */}
      {activeTab === 'status' && (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{t('accessRequest.myRequests')}</h5>
            <Button variant="outline-primary" size="sm" onClick={loadMyRequests} disabled={statusLoading}>
              {statusLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-2"></i>
                  {t('accessRequest.refreshStatus')}
                </>
              )}
            </Button>
          </Card.Header>
          <Card.Body>
            {statusLoading && myRequests.length === 0 ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">{t('accessRequest.table.loadingRequests')}</p>
              </div>
            ) : myRequests.length === 0 ? (
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                {t('accessRequest.table.noRequests')}
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>{t('accessRequest.table.ticketId')}</th>
                      <th>Summary</th>
                      <th>{t('common.status')}</th>
                      <th>{t('accessRequest.form.priority')}</th>
                      <th>{t('accessRequest.table.createdAt')}</th>
                      <th>{t('accessRequest.table.updatedAt')}</th>
                      <th>{t('accessRequest.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myRequests.map((request) => (
                      <tr key={request.key}>
                        <td>
                          <strong>{request.key}</strong>
                        </td>
                        <td>{request.summary}</td>
                        <td>
                          <Badge bg={getStatusBadgeVariant(request.status_category)}>{request.status}</Badge>
                        </td>
                        <td>
                          <Badge bg={request.priority === 'High' ? 'danger' : request.priority === 'Medium' ? 'warning' : 'secondary'}>
                            {request.priority || 'Medium'}
                          </Badge>
                        </td>
                        <td>{formatDate(request.created)}</td>
                        <td>{formatDate(request.updated)}</td>
                        <td>
                          <Button variant="outline-primary" size="sm" onClick={() => refreshRequestStatus(request.key)}>
                            <i className="fas fa-sync-alt me-1"></i>
                            {t('common.refresh')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
